import type { LiveBreakHost } from "@hobbyshopify/backend-client";
import type { ReactNode } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { formatCents } from "../../../../core/primitives/live";
import { StreamCycleActions, StreamCycleState } from "./stream-cycle-controller";

const isRipping = (box: LiveBreakHost | null) => box?.status === "RANDOMIZED" || box?.status === "BREAKING";

/** The box on screen, as a small picture over the camera. Its name and numbers are in the panel. */
export function BoxArt() {
  const state = useSubtree(StreamCycleState);
  const observer = useObserver();
  const { currentBox } = observer.watch(state.cycle);
  if (!currentBox?.imageUrl) return null;
  return (
    <img
      src={currentBox.imageUrl}
      alt={`${currentBox.title}, on screen`}
      className="h-20 w-auto shadow-[0_4px_14px_rgba(0,0,0,0.5)]"
    />
  );
}

/**
 * The panel along the bottom: what is on screen, how it is selling, and the one
 * button that names the next thing to do, always in the same place.
 */
export function StreamCycle({ offAirNote }: { offAirNote: ReactNode }) {
  const state = useSubtree(StreamCycleState);
  const actions = useSubtree(StreamCycleActions);
  const observer = useObserver();
  const { current, currentBox, next, step } = observer.watch(state.cycle);
  const live = observer.watch(state.live);
  const title = observer.watch(state.title);
  const failure = observer.watch(state.error);
  const confirming = observer.watch(state.confirming);

  const armed = step.confirm != null && confirming === step.confirm;
  const offAir = step.move === "goLive";
  const done = live && (step.move === "add" || step.move === "clear");
  const ripping = isRipping(currentBox);
  const total = currentBox?.slotCount ?? 0;
  const fill = total === 0 ? 0 : ((currentBox?.soldCount ?? 0) / total) * 100;
  const eyebrow = offAir ? (live ? "Off camera" : "Ready") : "On screen";
  const headline = offAir ? title : (current?.title ?? "Nothing yet");
  const nextLink = next && (
    <button type="button" className="shrink-0 font-bold text-[color:var(--color-accent)]" onClick={actions.openLineup}>
      Next: {next.title} ›
    </button>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[11px] font-extrabold tracking-[0.12em] text-white/50 uppercase">{eyebrow}</span>
          <div className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate text-[22px] font-extrabold tracking-[-0.01em]">{headline}</span>
            {!offAir && currentBox && (
              <span
                className={`shrink-0 rounded-md px-2 py-0.5 text-[11px] font-extrabold tracking-[0.06em] uppercase ${
                  ripping
                    ? "bg-[color:var(--color-danger)]/15 text-[color:var(--color-danger)]"
                    : "bg-[color:var(--color-accent)]/15 text-[color:var(--color-accent)]"
                }`}
              >
                {ripping ? "Ripping" : currentBox.status === "LOCKED" ? "Locked" : "On sale"}
              </span>
            )}
          </div>
        </div>
        {!offAir && currentBox && (
          <div className="flex shrink-0 flex-col items-end gap-0.5 tabular-nums">
            <span className="text-[11px] font-extrabold tracking-[0.12em] text-white/50 uppercase">Spots</span>
            <span className="text-[22px] font-extrabold">
              {currentBox.soldCount}
              <span className="text-[15px] text-white/50">/{currentBox.slotCount}</span>
            </span>
          </div>
        )}
      </div>

      {!offAir && currentBox ? (
        <div className="flex flex-col gap-2">
          <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
            <div
              className={`h-full transition-[width] duration-300 ${
                ripping ? "bg-[color:var(--color-danger)]" : "bg-[color:var(--color-accent)]"
              }`}
              style={{ width: `${fill}%` }}
            />
          </div>
          <div className="flex justify-between gap-3 text-[13px] text-white/60">
            <span className="min-w-0 truncate">
              {currentBox.soldCount >= currentBox.slotCount && currentBox.slotCount > 0 ? "Sold out · " : ""}
              {formatCents(currentBox.grossSoldCents)} taken
            </span>
            {nextLink}
          </div>
        </div>
      ) : (
        <div className="flex justify-between gap-3 text-sm text-white/65">
          <span>{offAir || step.move === "add" ? step.detail : current ? "" : "Nothing is on screen."}</span>
          {!offAir && nextLink}
        </div>
      )}

      <CycleButton />
      {done && (
        <button
          type="button"
          className="-mt-1 flex h-12 w-full items-center justify-center rounded-[10px] text-[15px] font-bold text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/10"
          onClick={actions.endStream}
        >
          End stream
        </button>
      )}
      {offAir ? (
        <div className="-mt-1 flex justify-center">{offAirNote}</div>
      ) : (
        step.move !== "add" && !armed && <span className="-mt-1 text-center text-xs text-white/55">{step.detail}</span>
      )}
      {failure && <p className="note note-danger">{failure}</p>}
    </div>
  );
}

/**
 * The one big button. Mint is safe to press; red cannot be undone, and asks
 * once, inline, with the consequence as the question.
 */
export function CycleButton() {
  const state = useSubtree(StreamCycleState);
  const actions = useSubtree(StreamCycleActions);
  const observer = useObserver();
  const { step } = observer.watch(state.cycle);
  const confirming = observer.watch(state.confirming);
  const busy = observer.watch(state.busy);

  const armed = step.confirm != null && confirming === step.confirm;
  const big = "h-14 w-full rounded-[10px] text-[17px] font-extrabold";

  if (armed) {
    return (
      <div className="flex flex-col gap-2.5 rounded-xl border border-[color:var(--color-danger)]/50 bg-[color:var(--color-danger)]/10 p-3">
        <span className="text-[13px] text-white/80">{step.detail}</span>
        <div className="flex gap-2">
          <button
            type="button"
            className={`btn-destructive h-12 flex-1 rounded-[10px] text-[15px] font-extrabold`}
            disabled={busy}
            onClick={actions.press}
          >
            {busy ? "Working…" : "Yes, break it early"}
          </button>
          <button type="button" className="btn-ghost h-12 px-5" onClick={actions.cancelConfirm}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      title={step.detail}
      className={`${step.danger ? "btn-destructive" : "btn-primary"} ${big}`}
      disabled={busy || step.disabled}
      onClick={actions.press}
    >
      {busy ? "Working…" : step.label}
    </button>
  );
}
